import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  deleteQuickReplyApi,
  generateQuickReplyTranslationsDraftApi,
  getQuickRepliesApi,
  getQuickReplyTranslationLanguagesApi,
  getQuickReplyTranslationsApi,
  postQuickReplyApi,
  saveQuickReplyTranslationsApi,
  updateQuickReplyApi,
} from "../../api/ecommerce/QuickReplyApi";

const initialState = {
  quickReply: null,
  quickReplies: [],
  translationDraft: null,
  translations: null,
  translationLanguages: [],
  savedTranslations: null,
  loading: false,
  saving: false,
  deleting: false,
  generatingTranslationDraft: false,
  loadingTranslations: false,
  loadingTranslationLanguages: false,
  savingTranslations: false,
  error: null,
};

const getErrorPayload = (err) => ({
  errorMessage:
    err?.response?.data?.message ||
    err?.response?.data?.error ||
    err?.message ||
    "Something went wrong",
});

export const getQuickReplies = createAsyncThunk(
  "quickReply/getQuickReplies",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await getQuickRepliesApi(payload);
      return response?.data;
    } catch (err) {
      return rejectWithValue(getErrorPayload(err));
    }
  }
);

export const postQuickReply = createAsyncThunk(
  "quickReply/postQuickReply",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await postQuickReplyApi(payload);
      return response?.data;
    } catch (err) {
      return rejectWithValue(getErrorPayload(err));
    }
  }
);

export const updateQuickReply = createAsyncThunk(
  "quickReply/updateQuickReply",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await updateQuickReplyApi(payload);
      return response?.data;
    } catch (err) {
      return rejectWithValue(getErrorPayload(err));
    }
  }
);

export const deleteQuickReply = createAsyncThunk(
  "quickReply/deleteQuickReply",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await deleteQuickReplyApi(payload);
      return response?.data;
    } catch (err) {
      return rejectWithValue(getErrorPayload(err));
    }
  }
);

export const generateQuickReplyTranslationsDraft = createAsyncThunk(
  "quickReply/generateQuickReplyTranslationsDraft",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await generateQuickReplyTranslationsDraftApi(payload);
      return response?.data;
    } catch (err) {
      return rejectWithValue(getErrorPayload(err));
    }
  }
);

export const saveQuickReplyTranslations = createAsyncThunk(
  "quickReply/saveQuickReplyTranslations",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await saveQuickReplyTranslationsApi(payload);
      return response?.data;
    } catch (err) {
      return rejectWithValue(getErrorPayload(err));
    }
  }
);

export const getQuickReplyTranslations = createAsyncThunk(
  "quickReply/getQuickReplyTranslations",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await getQuickReplyTranslationsApi(payload);
      return response?.data;
    } catch (err) {
      return rejectWithValue(getErrorPayload(err));
    }
  }
);

export const getQuickReplyTranslationLanguages = createAsyncThunk(
  "quickReply/getQuickReplyTranslationLanguages",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getQuickReplyTranslationLanguagesApi();
      return response?.data;
    } catch (err) {
      return rejectWithValue(getErrorPayload(err));
    }
  }
);

const quickReplySlice = createSlice({
  name: "quickReply",
  initialState,
  reducers: {
    clearQuickReplyError: (state) => {
      state.error = null;
    },
    clearQuickReplyState: (state) => {
      state.quickReply = null;
      state.quickReplies = [];
      state.translationDraft = null;
      state.translations = null;
      state.translationLanguages = [];
      state.savedTranslations = null;
      state.loading = false;
      state.saving = false;
      state.deleting = false;
      state.generatingTranslationDraft = false;
      state.loadingTranslations = false;
      state.loadingTranslationLanguages = false;
      state.savingTranslations = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getQuickReplies.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getQuickReplies.fulfilled, (state, action) => {
        state.quickReplies = action?.payload?.response ?? action?.payload;
        state.loading = false;
      })
      .addCase(getQuickReplies.rejected, (state, action) => {
        state.loading = false;
        state.error = action?.payload?.errorMessage || null;
      })
      .addCase(postQuickReply.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(postQuickReply.fulfilled, (state, action) => {
        state.quickReply = action?.payload?.response ?? action?.payload;
        state.saving = false;
      })
      .addCase(postQuickReply.rejected, (state, action) => {
        state.saving = false;
        state.error = action?.payload?.errorMessage || null;
      })
      .addCase(updateQuickReply.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(updateQuickReply.fulfilled, (state, action) => {
        state.quickReply = action?.payload?.response ?? action?.payload;
        state.saving = false;
      })
      .addCase(updateQuickReply.rejected, (state, action) => {
        state.saving = false;
        state.error = action?.payload?.errorMessage || null;
      })
      .addCase(deleteQuickReply.pending, (state) => {
        state.deleting = true;
        state.error = null;
      })
      .addCase(deleteQuickReply.fulfilled, (state) => {
        state.deleting = false;
      })
      .addCase(deleteQuickReply.rejected, (state, action) => {
        state.deleting = false;
        state.error = action?.payload?.errorMessage || null;
      })
      .addCase(generateQuickReplyTranslationsDraft.pending, (state) => {
        state.generatingTranslationDraft = true;
        state.error = null;
      })
      .addCase(generateQuickReplyTranslationsDraft.fulfilled, (state, action) => {
        state.translationDraft = action?.payload?.response ?? action?.payload;
        state.generatingTranslationDraft = false;
      })
      .addCase(generateQuickReplyTranslationsDraft.rejected, (state, action) => {
        state.generatingTranslationDraft = false;
        state.error = action?.payload?.errorMessage || null;
      })
      .addCase(saveQuickReplyTranslations.pending, (state) => {
        state.savingTranslations = true;
        state.error = null;
      })
      .addCase(saveQuickReplyTranslations.fulfilled, (state, action) => {
        state.savedTranslations = action?.payload?.response ?? action?.payload;
        state.savingTranslations = false;
      })
      .addCase(saveQuickReplyTranslations.rejected, (state, action) => {
        state.savingTranslations = false;
        state.error = action?.payload?.errorMessage || null;
      })
      .addCase(getQuickReplyTranslations.pending, (state) => {
        state.loadingTranslations = true;
        state.error = null;
      })
      .addCase(getQuickReplyTranslations.fulfilled, (state, action) => {
        state.translations = action?.payload?.response ?? action?.payload;
        state.loadingTranslations = false;
      })
      .addCase(getQuickReplyTranslations.rejected, (state, action) => {
        state.loadingTranslations = false;
        state.error = action?.payload?.errorMessage || null;
      })
      .addCase(getQuickReplyTranslationLanguages.pending, (state) => {
        state.loadingTranslationLanguages = true;
        state.error = null;
      })
      .addCase(getQuickReplyTranslationLanguages.fulfilled, (state, action) => {
        state.translationLanguages = action?.payload?.response ?? action?.payload;
        state.loadingTranslationLanguages = false;
      })
      .addCase(getQuickReplyTranslationLanguages.rejected, (state, action) => {
        state.loadingTranslationLanguages = false;
        state.error = action?.payload?.errorMessage || null;
      });
  },
});

export const { clearQuickReplyError, clearQuickReplyState } =
  quickReplySlice.actions;

export default quickReplySlice.reducer;

